import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sparkle, ShieldCheck, Warning, CheckCircle, ArrowRight, X,
  Buildings, CurrencyDollar,
} from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * BrokerOnboardingCard — dashboard nudge card that surfaces when the pilot
 * has one or more UNVERIFIED broker names on their invoices/loads. Clicking
 * the primary broker opens a 30-second wizard that:
 *   1. Prompts for MC# / DOT#
 *   2. Runs an FMCSA credential check
 *   3. Shows the customer's credit scorecard from existing invoice history
 *   4. Saves + dismisses
 *
 * The card auto-hides when the vault is fully caught up. Sits on the main
 * dashboard so pilots see the nudge on their first login of the day.
 */
export default function BrokerOnboardingCard() {
  const [top, setTop] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/broker-credentials/report/unverified");
      const list = data.unverified || [];
      setTop(list[0] || null);
      setRemaining(Math.max(0, list.length - 1));
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading || !top) return null;

  return (
    <>
      <section
        className="rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/[0.08] to-transparent p-5"
        data-testid="broker-onboarding-card"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="rounded-full bg-amber-500/15 p-2 shrink-0">
              <Sparkle size={16} weight="fill" className="text-amber-300" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-amber-300">
                Onboard broker · 30-second wizard
              </div>
              <h3 className="text-white text-base font-semibold mt-1 truncate">
                Verify <span className="text-amber-200">{top.customer_name}</span> before your next load
              </h3>
              <div className="text-xs text-white/60 mt-1">
                {top.invoice_count + top.load_count} touch{(top.invoice_count + top.load_count) !== 1 ? "es" : ""} on record ·
                FMCSA credential check + credit review in one go.
                {remaining > 0 && (
                  <span className="text-amber-300/80 ml-1">+ {remaining} more waiting</span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-4 py-2 text-xs font-semibold text-black hover:bg-amber-300"
            data-testid="broker-onboarding-start"
          >
            Start wizard <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </section>
      {modalOpen && (
        <OnboardingWizard
          broker={top}
          onClose={() => setModalOpen(false)}
          onDone={() => { setModalOpen(false); load(); }}
        />
      )}
    </>
  );
}


// ---------------- Wizard ----------------
function OnboardingWizard({ broker, onClose, onDone }) {
  const [step, setStep] = useState(1);         // 1: verify, 2: credit, 3: done
  const [mc, setMc] = useState("");
  const [dot, setDot] = useState("");
  const [busy, setBusy] = useState(false);
  const [credential, setCredential] = useState(null);
  const [credit, setCredit] = useState(null);

  const runVerify = async () => {
    if (!mc && !dot) { toast.error("Enter an MC# or DOT#"); return; }
    setBusy(true);
    try {
      const { data } = await client.post("/broker-credentials/verify", {
        customer_name: broker.customer_name,
        mc_number: mc.trim() || undefined,
        dot_number: dot.trim() || undefined,
      });
      setCredential(data);
      if (!data.verified) {
        toast.error(data.verification?.reason || "Not found in FMCSA");
      } else if (data.out_of_service) {
        toast.warning(`${data.legal_name || broker.customer_name} is OUT OF SERVICE`);
      } else {
        toast.success(`Verified · ${data.legal_name || broker.customer_name} · ${data.operating_status}`);
      }
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const loadCredit = async () => {
    setBusy(true);
    try {
      // Pull the credit scorecard row for this customer name from the aggregate list
      const { data } = await client.get("/customer-credit");
      const found = (data.rows || []).find(
        (r) => (r.customer_name || "").toLowerCase() === broker.customer_name.toLowerCase(),
      );
      setCredit(found || {
        customer_name: broker.customer_name,
        score: null, tier: "new", loads: 0, revenue: 0,
        avg_days_to_pay: null, is_new: true,
      });
    } catch { setCredit(null); }
    finally { setBusy(false); }
  };

  useEffect(() => { if (step === 2) loadCredit(); /* eslint-disable-next-line */ }, [step]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#111] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="onboarding-wizard"
      >
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#FF5A1F]">
              Broker onboarding · Step {step}/3
            </div>
            <h2 className="text-xl font-bold text-white mt-1">{broker.customer_name}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 text-white/50 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="mt-4 flex gap-1.5">
          {[1, 2, 3].map((n) => (
            <div key={n}
              className={`h-1 flex-1 rounded-full ${
                step >= n ? "bg-[#FF5A1F]" : "bg-white/10"
              }`}
            />
          ))}
        </div>

        {step === 1 && (
          <div className="mt-6 space-y-4" data-testid="wizard-step-1">
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs text-white/70 flex items-start gap-2">
              <ShieldCheck size={14} className="text-[#FF5A1F] shrink-0 mt-0.5" />
              <span>Look up their MC# or DOT# from the rate confirmation you received — it's usually near the top.</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="MC #">
                <input
                  value={mc} onChange={(e) => setMc(e.target.value)}
                  placeholder="79658"
                  className="input-dark" data-testid="wizard-mc-input"
                />
              </Field>
              <Field label="DOT #">
                <input
                  value={dot} onChange={(e) => setDot(e.target.value)}
                  placeholder="125550"
                  className="input-dark" data-testid="wizard-dot-input"
                />
              </Field>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/70 hover:bg-white/5">
                Skip for now
              </button>
              <button
                onClick={runVerify} disabled={busy || (!mc && !dot)}
                className="inline-flex items-center gap-2 rounded-lg bg-[#FF5A1F] px-4 py-2 text-sm font-semibold text-white hover:bg-[#FF5A1F]/90 disabled:opacity-50"
                data-testid="wizard-run-verify"
              >
                <ShieldCheck size={14} weight="fill" />
                {busy ? "Checking FMCSA…" : "Run FMCSA check"}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="mt-6 space-y-4" data-testid="wizard-step-2">
            {credential && (
              <div className={`rounded-lg border p-3 ${
                !credential.verified || credential.out_of_service
                  ? "border-red-500/30 bg-red-500/[0.05]"
                  : "border-emerald-500/30 bg-emerald-500/[0.05]"
              }`}>
                <div className="flex items-center gap-2">
                  {credential.verified && !credential.out_of_service ? (
                    <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                  ) : (
                    <Warning size={16} weight="fill" className="text-red-400" />
                  )}
                  <div className="text-sm font-semibold text-white">
                    {credential.verified
                      ? (credential.out_of_service ? "OUT OF SERVICE" : credential.operating_status)
                      : "Not found in FMCSA"}
                  </div>
                </div>
                {credential.legal_name && (
                  <div className="text-xs text-white/60 mt-1">
                    Legal: <b className="text-white/80">{credential.legal_name}</b>
                  </div>
                )}
                {credential.verification?.power_units > 0 && (
                  <div className="text-xs text-white/50 mt-0.5">
                    {credential.verification.power_units} power units · {credential.verification.drivers} drivers
                  </div>
                )}
              </div>
            )}
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
              <div className="flex items-center gap-2 mb-2">
                <CurrencyDollar size={14} className="text-emerald-400" />
                <div className="text-xs font-semibold uppercase tracking-widest text-white/70">
                  Credit history from your invoices
                </div>
              </div>
              {busy ? (
                <div className="text-xs text-white/50">Loading…</div>
              ) : credit && !credit.is_new ? (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <MicroStat label="Score" value={credit.score || "—"} tone={
                    credit.tier === "excellent" ? "emerald" :
                    credit.tier === "good" ? "amber" : "rose"
                  } />
                  <MicroStat label="Loads" value={credit.loads || 0} />
                  <MicroStat label="Avg days to pay" value={credit.avg_days_to_pay || "—"} />
                </div>
              ) : (
                <div className="text-xs text-white/50">
                  No invoice history yet — this broker will build a credit score once you run
                  your first load with them.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setStep(3)}
                className="inline-flex items-center gap-2 rounded-lg bg-[#FF5A1F] px-4 py-2 text-sm font-semibold text-white hover:bg-[#FF5A1F]/90"
                data-testid="wizard-continue"
              >
                Continue <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="mt-6 space-y-4 text-center" data-testid="wizard-step-3">
            <div className="rounded-full bg-emerald-500/15 p-4 w-16 h-16 mx-auto flex items-center justify-center">
              <CheckCircle size={28} weight="fill" className="text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">Broker onboarded</h3>
            <div className="text-sm text-white/60 max-w-sm mx-auto">
              {broker.customer_name} is now in your credentials vault. You can view or re-verify anytime
              from the Customer Credit page.
            </div>
            <button
              onClick={onDone}
              className="inline-flex items-center gap-2 rounded-lg bg-[#FF5A1F] px-4 py-2 text-sm font-semibold text-white hover:bg-[#FF5A1F]/90"
              data-testid="wizard-done"
            >
              Done
            </button>
          </div>
        )}
      </div>
      <style>{`
        .input-dark {
          width: 100%; border-radius: 8px;
          border: 1px solid rgba(255,255,255,0.1);
          background: rgba(0,0,0,0.4); padding: 8px 12px;
          font-size: 13px; color: white; outline: none;
        }
        .input-dark:focus { border-color: #FF5A1F; }
      `}</style>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-white/60 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}

function MicroStat({ label, value, tone }) {
  const toneClass = {
    emerald: "text-emerald-300",
    amber: "text-amber-300",
    rose: "text-rose-300",
  }[tone] || "text-white";
  return (
    <div className="rounded border border-white/5 bg-white/[0.02] p-2">
      <div className={`text-lg font-semibold tabular-nums ${toneClass}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-white/40 mt-0.5">{label}</div>
    </div>
  );
}
